import React, { useState } from 'react'
import { useTheme } from '../../../../components/hooks/useTheme';
import { Button, Dialog } from '@mui/material';
import axios from 'axios';
import baseURL from '../../../../configs/api';
import PleaseWaitPopup from '../../../PleaseWaitPopup';
import colorConfigs from '../../../../configs/colorConfigs';
import EmailValidatedComponent from '../../../../components/EmailValidatedComponent';
import ReactQuill from 'react-quill';
import Cookies from 'js-cookie';
import useTenant from '../../../../components/hooks/useTenant';
import useAuth from '../../../../components/hooks/useAuth';

const modules = {
    toolbar: [
        [{ font: [] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike"],
        [{ color: [] }, { background: [] }],
        [{ script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ indent: "-1" }, { indent: "+1" }, { align: [] }],
        ["link", "image", "video"],
        ["clean"],
    ],
};

type AddSendEmailChecklistProps = {
    fileNameForEmail: string;
    openAddSendEmailVehicleFile: boolean;
    setOpenAddSendEmailVehicleFile: (value: boolean) => void;
    setRefreshSendEmailVehicleFile: (value: boolean) => void;
}

export const SendEmailVehicleFile = ({ fileNameForEmail, openAddSendEmailVehicleFile, setOpenAddSendEmailVehicleFile, setRefreshSendEmailVehicleFile }: AddSendEmailChecklistProps) => {
    const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
    const { handleTenantError }: any = useTenant();
    const { auth }: any = useAuth();
    const { themeStyles } = useTheme();
    const [sendEmailTo, setSendEmailTo] = useState<string>("");
    const [sendEmailCc, setSendEmailCc] = useState<string>("");
    const [sendEmailReplyTo, setSendEmailReplyTo] = useState<string>("");
    const [sendEmailSubject, setSendEmailSubject] = useState<string>("");
    const [content, setContent] = useState<string>("");
    const [pleaseWait, setPleaseWait] = useState<boolean>(false);

    const resetForm = () => {
        setSendEmailTo("");
        setSendEmailCc("");
        setSendEmailReplyTo("");
        setSendEmailSubject("");
        setContent("");
    };

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!pleaseWait) {
            setPleaseWait(true);
            axios
                .post(
                    `${baseURL}sendEmailWithAttachment?userId=${auth?.id}`,
                    JSON.stringify({
                        filePath: `${baseURL}vehicleFile/Get?name=${fileNameForEmail}`,
                        toEmail: sendEmailTo,
                        ccEmail: sendEmailCc,
                        replyEmail: sendEmailReplyTo,
                        subject: sendEmailSubject,
                        comment: content
                    }),
                    {
                        headers: {
                            'X-Tenant-ID': tenantId,
                            "Content-Type": "application/json"
                        },
                        withCredentials: true,
                    }
                )
                .then(() => {
                    resetForm();
                    setPleaseWait(false);
                    setOpenAddSendEmailVehicleFile(false);
                    setRefreshSendEmailVehicleFile(true);
                    handleTenantError(null);
                })
                .catch((error) => {
                    setPleaseWait(false);
                    handleTenantError(error);
                });
        }
    };

    return (
        <>
            <Dialog
                open={openAddSendEmailVehicleFile}
                maxWidth="md"
                fullScreen={false}
                sx={{
                    '& .MuiDialog-paper': {
                        maxWidth: '550px',
                        width: '100%',
                        borderRadius: '16px',
                    },
                }}
            >
                <div className={`px-[10px] py-[5px] h-full ${themeStyles.main.tailwindSectionBg}`}>
                    <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
                        <div className="w-full mt-2">
                            <div className={`w-full`}>
                                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>

                                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Send Email</h2>

                                    <span className={`material-symbols-outlined text-[25px] font-light cursor-pointer ${themeStyles.main.textHeadingColor}`} onClick={() => setOpenAddSendEmailVehicleFile(false)}>close</span>
                                </div>

                                <div className="mt-2 p-2">
                                    <div className="w-full">
                                        <form onSubmit={handleSubmit}>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>To</label>
                                                    <EmailValidatedComponent
                                                        text={sendEmailTo}
                                                        setText={setSendEmailTo}
                                                        required={true}
                                                    />
                                                </div>

                                                <div />

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>CC</label>
                                                    <EmailValidatedComponent
                                                        text={sendEmailCc}
                                                        setText={setSendEmailCc}
                                                    />
                                                </div>

                                                <div>
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Reply To</label>
                                                    <EmailValidatedComponent
                                                        text={sendEmailReplyTo}
                                                        setText={setSendEmailReplyTo}
                                                    />
                                                </div>

                                                <div className="col-span-2">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Subject</label>
                                                    <input
                                                        type="text"
                                                        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                                        value={sendEmailSubject}
                                                        onChange={(e) => setSendEmailSubject(e.target.value)}
                                                        required
                                                    />
                                                </div>

                                                <div className="col-span-2 relative">
                                                    <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Content</label>
                                                    <ReactQuill
                                                        style={{ height: "150px", width: "100%" }}
                                                        theme="snow"
                                                        value={content}
                                                        modules={modules}
                                                        onChange={setContent}
                                                    />
                                                </div>
                                            </div>

                                            <div className="flex gap-4 mt-[6rem]">
                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.cancel.bg,
                                                        border: colorConfigs.btn.cancel.border,
                                                        color: colorConfigs.btn.cancel.color
                                                    }}
                                                    onClick={() => setOpenAddSendEmailVehicleFile(false)}
                                                >
                                                    Cancel
                                                </Button>

                                                <Button variant="contained"
                                                    sx={{
                                                        backgroundColor: colorConfigs.btn.main.bg
                                                    }}
                                                    type="submit"
                                                >
                                                    Send Email
                                                </Button>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>

            <PleaseWaitPopup pleaseWaitPopup={pleaseWait} />
        </>
    )
}